import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  // Data Display
  ChipComponent,
  AvatarComponent,
  AvatarGroupComponent,
  ListComponent,
  DataTableComponent,
  TimelineComponent,
  CarouselComponent,
  TreeViewComponent,
  TreeTableComponent,
  // Navigation
  BreadcrumbComponent,
  PaginationComponent,
  MenuComponent,
  DropdownMenuComponent,
  // Utility
  LoadingOverlayComponent,
  ClipboardComponent,
  DragDropComponent,
  // Advanced
  ChartsComponent,
  RichTextEditorComponent,
  SchedulerComponent,
  KanbanComponent,
  // Layout
  CardComponent,
  ButtonComponent
} from '../ui';

@Component({
  selector: 'app-showcase-page2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ChipComponent,
    AvatarComponent,
    AvatarGroupComponent,
    ListComponent,
    DataTableComponent,
    TimelineComponent,
    CarouselComponent,
    TreeViewComponent,
    TreeTableComponent,
    BreadcrumbComponent,
    PaginationComponent,
    MenuComponent,
    DropdownMenuComponent,
    LoadingOverlayComponent,
    ClipboardComponent,
    DragDropComponent,
    ChartsComponent,
    RichTextEditorComponent,
    SchedulerComponent,
    KanbanComponent,
    CardComponent,
    ButtonComponent
  ],
  template: `
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div class="max-w-7xl mx-auto">
        <h1 class="text-4xl font-bold text-gray-900 dark:text-white mb-2">UI Component Showcase - Page 2</h1>
        <p class="text-gray-600 dark:text-gray-400 mb-8">Data Display, Navigation, Utility & Advanced Components</p>

        <!-- Breadcrumb -->
        <app-card header="Breadcrumb" class="mb-6">
          <app-breadcrumb [items]="breadcrumbItems" />
        </app-card>

        <!-- Chips -->
        <app-card header="Chips" class="mb-6">
          <div class="flex flex-wrap gap-2">
            <app-chip label="Primary" variant="primary" />
            <app-chip label="Success" variant="success" />
            <app-chip label="Danger" variant="danger" />
            <app-chip label="Warning" variant="warning" />
            <app-chip label="Info" variant="info" />
            <app-chip label="Removable" variant="primary" [removable]="true" />
          </div>
        </app-card>

        <!-- Avatars -->
        <app-card header="Avatars" class="mb-6">
          <div class="space-y-4">
            <div class="flex items-center gap-4">
              <app-avatar size="xs" label="JD" />
              <app-avatar size="sm" label="JD" />
              <app-avatar size="md" label="JD" />
              <app-avatar size="lg" label="JD" />
              <app-avatar size="xl" label="JD" />
            </div>
            <app-avatar-group [avatars]="avatarGroupItems" />
          </div>
        </app-card>

        <!-- List -->
        <app-card header="List" class="mb-6">
          <app-list [items]="listItems" />
        </app-card>

        <!-- Data Table -->
        <app-card header="Data Table" class="mb-6">
          <app-data-table 
            [columns]="tableColumns"
            [data]="tableData" />
        </app-card>

        <!-- Timeline -->
        <app-card header="Timeline" class="mb-6">
          <app-timeline [items]="timelineItems" />
        </app-card>

        <!-- Tree View -->
        <app-card header="Tree View" class="mb-6">
          <app-tree-view [nodes]="treeNodes" (nodeSelect)="onTreeNodeSelect($event)" />
        </app-card>

        <!-- Tree Table -->
        <app-card header="Tree Table" class="mb-6">
          <app-tree-table 
            [columns]="treeTableColumns"
            [nodes]="treeTableNodes" />
        </app-card>

        <!-- Pagination -->
        <app-card header="Pagination" class="mb-6">
          <app-pagination 
            [currentPage]="currentPage()"
            [totalItems]="100"
            [itemsPerPage]="10"
            (pageChangeEvent)="onPageChange($event)" />
        </app-card>

        <!-- Menu -->
        <app-card header="Menu" class="mb-6">
          <app-menu [items]="menuItems" />
        </app-card>

        <!-- Dropdown Menu -->
        <app-card header="Dropdown Menu" class="mb-6">
          <app-dropdown-menu [items]="dropdownMenuItems">
            <button trigger app-button variant="primary">Open Menu</button>
          </app-dropdown-menu>
        </app-card>

        <!-- Clipboard -->
        <app-card header="Clipboard" class="mb-6">
          <app-clipboard text="Copy this text!" />
        </app-card>

        <!-- Loading Overlay -->
        <app-card header="Loading Overlay" class="mb-6">
          <div class="relative h-32 bg-gray-100 dark:bg-gray-800 rounded">
            <app-loading-overlay [visible]="showOverlay()" />
            <div class="p-4">
              <app-button variant="primary" (clicked)="showOverlay.set(!showOverlay())">
                Toggle Overlay
              </app-button>
            </div>
          </div>
        </app-card>

        <!-- Drag & Drop -->
        <app-card header="Drag & Drop" class="mb-6">
          <app-drag-drop [items]="dragDropItems()" (reorderEvent)="onReorder($event)">
            <div class="p-4 border rounded bg-white dark:bg-gray-800">
              Drag and drop items
            </div>
          </app-drag-drop>
        </app-card>

        <!-- Charts -->
        <app-card header="Charts" class="mb-6">
          <app-charts 
            title="Sample Chart"
            [type]="'bar'"
            [data]="chartData"
            [height]="'300px'" />
        </app-card>

        <!-- Rich Text Editor -->
        <app-card header="Rich Text Editor" class="mb-6">
          <app-rich-text-editor 
            [(ngModel)]="richTextContent"
            [placeholder]="'Enter your text here...'" />
        </app-card>

        <!-- Scheduler -->
        <app-card header="Scheduler" class="mb-6">
          <app-scheduler [events]="schedulerEvents" />
        </app-card>

        <!-- Kanban -->
        <app-card header="Kanban Board" class="mb-6">
          <app-kanban [columns]="kanbanColumns()" (itemMoveEvent)="onKanbanMove($event)" />
        </app-card>

        <!-- Carousel -->
        <app-card header="Carousel" class="mb-6">
          <app-carousel [items]="carouselItems" />
        </app-card>
      </div>
    </div>
  `,
  styles: []
})
export class ShowcasePage2Component {
  showOverlay = signal(false);
  currentPage = signal(1);
  richTextContent = '';

  breadcrumbItems = [
    { label: 'Home', route: '/' },
    { label: 'Components', route: '/components' },
    { label: 'Showcase', route: '/showcase' }
  ];

  listItems = [
    { id: '1', label: 'List Item 1', description: 'Description 1' },
    { id: '2', label: 'List Item 2', description: 'Description 2' },
    { id: '3', label: 'List Item 3', description: 'Description 3' }
  ];

  tableColumns = [
    { field: 'id', header: 'ID' },
    { field: 'name', header: 'Name' },
    { field: 'email', header: 'Email' },
    { field: 'status', header: 'Status' }
  ];

  tableData = [
    { id: 1, name: 'John Doe', email: 'john@example.com', status: 'Active' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'Active' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', status: 'Inactive' },
    { id: 4, name: 'Alice Brown', email: 'alice@example.com', status: 'Active' },
    { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', status: 'Active' }
  ];

  timelineItems = [
    { title: 'Event 1', description: 'Description 1', date: '2024-01-01' },
    { title: 'Event 2', description: 'Description 2', date: '2024-02-01' },
    { title: 'Event 3', description: 'Description 3', date: '2024-03-01' }
  ];

  treeNodes = [
    {
      id: '1',
      label: 'Node 1',
      children: [
        { id: '1-1', label: 'Child 1-1' },
        { id: '1-2', label: 'Child 1-2' }
      ]
    },
    {
      id: '2',
      label: 'Node 2',
      children: [
        { id: '2-1', label: 'Child 2-1' },
        { id: '2-2', label: 'Child 2-2' }
      ]
    }
  ];

  treeTableColumns = [
    { field: 'name', header: 'Name' },
    { field: 'type', header: 'Type' },
    { field: 'size', header: 'Size' }
  ];

  treeTableNodes = [
    {
      id: '1',
      data: { name: 'Folder 1', type: 'Folder', size: '-' },
      children: [
        { id: '1-1', data: { name: 'File 1-1.txt', type: 'File', size: '10 KB' } }
      ]
    }
  ];

  menuItems = [
    { label: 'Home', route: '/', icon: 'M3 12l2-2m0 0l7-7 7 7M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7' },
    { label: 'About', route: '/about', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { label: 'Contact', route: '/contact', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' }
  ];

  dropdownMenuItems = [
    { label: 'Edit', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
    { label: 'Delete', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' },
    { label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' }
  ];

  avatarGroupItems = [
    { label: 'JD', src: undefined },
    { label: 'JS', src: undefined },
    { label: 'BJ', src: undefined }
  ];

  dragDropItems = signal([
    { id: '1', data: { name: 'Item 1' } },
    { id: '2', data: { name: 'Item 2' } },
    { id: '3', data: { name: 'Item 3' } }
  ]);

  chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
    datasets: [{
      label: 'Sales',
      data: [65, 59, 80, 81, 56],
      backgroundColor: '#3b82f6'
    }]
  };

  schedulerEvents = [
    {
      id: '1',
      title: 'Meeting',
      start: new Date('2024-12-01T10:00:00'),
      end: new Date('2024-12-01T11:00:00'),
      color: '#3b82f6'
    }
  ];

  kanbanColumns = signal([
    {
      id: 'todo',
      title: 'To Do',
      items: [
        { id: '1', title: 'Task 1', description: 'Description 1' },
        { id: '2', title: 'Task 2', description: 'Description 2' }
      ]
    },
    {
      id: 'in-progress',
      title: 'In Progress',
      items: [
        { id: '3', title: 'Task 3', description: 'Description 3' }
      ]
    },
    {
      id: 'done',
      title: 'Done',
      items: [
        { id: '4', title: 'Task 4', description: 'Description 4' }
      ]
    }
  ]);

  carouselItems = [
    { id: '1', src: 'https://via.placeholder.com/800x400?text=Slide+1', alt: 'Slide 1' },
    { id: '2', src: 'https://via.placeholder.com/800x400?text=Slide+2', alt: 'Slide 2' },
    { id: '3', src: 'https://via.placeholder.com/800x400?text=Slide+3', alt: 'Slide 3' }
  ];

  onTreeNodeSelect(node: any): void {
    console.log('Tree node selected:', node);
  }

  onReorder(items: any[]): void {
    this.dragDropItems.set(items);
  }

  onKanbanMove(event: any): void {
    console.log('Kanban item moved:', event);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }
}
